import React, { useEffect, useMemo, useState } from "react";
import "./QuickQuestionFilter.css";

function buildDraftFilters(groups = [], selectedFilters = {}) {
  return (groups || []).reduce((draftFilters, group) => {
    if (selectedFilters?.[group.filterKey] !== undefined) {
      draftFilters[group.filterKey] = selectedFilters[group.filterKey];
    }

    return draftFilters;
  }, {});
}

const QuickQuestionFilter = ({
  groups = [],
  selectedFilters = {},
  onApplyFilters,
  onReset,
  onClose,
  className = "",
  isLoading = false,
  isOpen = false,
}) => {
  const [openDropdown, setOpenDropdown] = useState(() => groups?.[0]?.id || null);
  const resolvedGroups = useMemo(() => groups || [], [groups]);
  const [draftFilters, setDraftFilters] = useState(() =>
    buildDraftFilters(groups, selectedFilters)
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setDraftFilters(buildDraftFilters(resolvedGroups, selectedFilters));
    setOpenDropdown((current) => current || resolvedGroups?.[0]?.id || null);
  }, [isOpen, resolvedGroups, selectedFilters]);

  const handleDropdownToggle = (groupId) => {
    setOpenDropdown((current) => (current === groupId ? null : groupId));
  };

  const handleOptionSelect = (group, option) => {
    setDraftFilters((current) => ({
      ...current,
      [group.filterKey]: option.value,
    }));
  };

  const handleReset = () => {
    onReset?.();
    setOpenDropdown(resolvedGroups?.[0]?.id || null);
  };

  const handleApply = () => {
    onApplyFilters?.(draftFilters);
    onClose?.();
  };

  if (isLoading) {
    return (
      <aside className={`qbf ${className}`.trim()} aria-hidden="true">
        <div className="qbf__header">
          <div className="d-flex align-items-center gap-2">
            <span className="dashboard-skeleton dashboard-skeleton--icon filter-card__icon" />
            <div className="dashboard-skeleton-stack" style={{ minWidth: 160 }}>
              <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--title qbf__shimmer-title" />
              <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--text-short qbf__shimmer-subtitle" />
            </div>
          </div>

          <span className="dashboard-skeleton dashboard-skeleton--icon qbf__close d-flex d-xl-none" />
        </div>

        <div className="qbf__groups">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={`qbf-shimmer-${index}`} className="qbf__groupWrap">
              <div className="qbf__group">
                <span className="dashboard-skeleton dashboard-skeleton--icon qbf__groupIcon" />
                <div className="dashboard-skeleton-stack" style={{ flex: 1 }}>
                  <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--title qbf__shimmer-line" />
                  <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--text-short qbf__shimmer-line qbf__shimmer-line--short" />
                </div>
                <span className="dashboard-skeleton dashboard-skeleton--icon qbf__shimmer-chevron" />
              </div>
            </div>
          ))}
        </div>

        <div className="qbf__actions">
          <span className="dashboard-skeleton dashboard-skeleton--button qbf__shimmer-action" />
          <span className="dashboard-skeleton dashboard-skeleton--button qbf__shimmer-action" />
        </div>
      </aside>
    );
  }

  return (
    <aside className={`qbf ${className}`.trim()}>
      <div className="qbf__header">
        <div className="d-flex align-items-center gap-2">
          <div className="filter-card__icon">
            <i className="bi bi-sliders2"></i>
          </div>
          <div>
            <h2 className="qbf__title">Quick Question Filter</h2>
            <p className="qbf__subtitle">Refine your results</p>
          </div>
        </div>

        <button
          type="button"
          className="qbf__close d-flex d-xl-none"
          onClick={onClose}
          aria-label="Close filters"
        >
          <i className="bi bi-x-lg" />
        </button>
      </div>

      <div className="qbf__groups">
        {resolvedGroups.map((group) => {
          const isOpen = openDropdown === group.id;
          const selectedValue =
            group.options.find((option) => draftFilters?.[group.filterKey] === option.value)?.label ||
            group.defaultValue ||
            "All";

          return (
            <div key={group.id} className={`qbf__groupWrap ${isOpen ? "is-open" : ""}`}>
              <button
                type="button"
                className="qbf__group"
                onClick={() => handleDropdownToggle(group.id)}
                aria-expanded={isOpen}
              >
                <span className="qbf__groupIcon">
                  <i className={group.icon} />
                </span>

                <span className="qbf__groupCopy">
                  <strong>{group.title}</strong>
                  <span>{selectedValue}</span>
                </span>

                <i
                  className={`bi ${isOpen ? "bi-chevron-up" : "bi-chevron-down"} qbf__chevron`}
                />
              </button>

              {isOpen ? (
                <div className="qbf__dropdown">
                  {group.options.map((option) => (
                    <button
                      type="button"
                      key={option.id}
                      className={`qbf__option ${draftFilters?.[group.filterKey] === option.value ? "is-active" : ""}`}
                      onClick={() => handleOptionSelect(group, option)}
                    >
                      <span>{option.label}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="qbf__actions">
        <button type="button" className="qbf__apply" onClick={handleApply}>
          <i className="bi bi-funnel" />
          Apply Filters
        </button>

        <button type="button" className="qbf__reset" onClick={handleReset}>
          <i className="bi bi-arrow-clockwise" />
          Reset Filters
        </button>
      </div>
    </aside>
  );
};

export default QuickQuestionFilter;
